
import { useState, useEffect, useMemo } from 'react';
import {
    Container, Box, Typography, Paper, Button,
    Card, CardContent, IconButton, Divider,
    Chip, CircularProgress, FormControl, Select, MenuItem,
    TextField, Skeleton, Fade, Tooltip
} from '@mui/material';
import {
    ShoppingCart as ShoppingCartIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Delete as DeleteIcon,
    ShoppingBag as ShoppingBagIcon,
    LocalOffer as CouponIcon,
    CheckCircle as CheckCircleIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';
import cartService from '../services/cartService';
import api from '../services/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import '../styles/ProtectedRoutes.css';

// Fallback image shown if a product photo fails to load
const FALLBACK_IMAGE =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
            <rect width="100%" height="100%" fill="#2a2a2a"/>
            <text x="50%" y="50%" fill="#bc965e" font-size="14" font-family="sans-serif"
                  text-anchor="middle" dominant-baseline="middle">No image</text>
        </svg>`
    );

const formatINR = (value) =>
    `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const CartPage = () => {
    const [cartItems, setCartItems]           = useState([]);
    const [loading, setLoading]               = useState(true);
    const [updating, setUpdating]             = useState(false);
    const [couponCode, setCouponCode]         = useState('');
    const [appliedCoupon, setAppliedCoupon]   = useState(null);
    const [applyingCoupon, setApplyingCoupon] = useState(false);
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [showCoupons, setShowCoupons]       = useState(false);

    const { setCartCount } = useCart();
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        loadCart();
        loadAvailableCoupons();
    }, []);

    const loadCart = async () => {
        try {
            setLoading(true);
            const response = await cartService.getCart();
            if (response.success) {
                setCartItems(response.data.items);
                setCartCount(response.data.itemCount || 0);
            }
        } catch (error) {
            toast.error(error.message || 'Failed to load cart');
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableCoupons = async () => {
        try {
            const res = await api.get('/coupons/available');
            if (res.data.success) setAvailableCoupons(res.data.data);
        } catch (error) {
            console.error('Failed to load coupons');
        }
    };

    const getItemDisplaySubtotal = (item) => {
        if (
            appliedCoupon?.ApplicableTo === 'product' &&
            appliedCoupon.AppliedOnCode === item.ProductCode
        ) {
            const discounted = parseFloat(item.Subtotal) - parseFloat(appliedCoupon.DiscountAmount);
            return Math.max(discounted, 0);
        }
        return parseFloat(item.Subtotal);
    };

    const handleQuantityChange = async (cartId, currentQty, change) => {
        const newQty = currentQty + change;
        if (newQty < 1) return;
        try {
            setUpdating(true);
            await cartService.updateQuantity(cartId, newQty);
            await loadCart();
            setAppliedCoupon(null);
            setCouponCode('');
            toast.success('Cart updated');
        } catch (error) {
            toast.error(error.message || 'Failed to update cart');
        } finally {
            setUpdating(false);
        }
    };

    const handleSizeChange = async (cartId, newSize) => {
        try {
            setUpdating(true);
            await cartService.updateSize(cartId, newSize);
            await loadCart();
            toast.success('Size updated');
        } catch (error) {
            toast.error(error.message || 'Failed to update size');
        } finally {
            setUpdating(false);
        }
    };

    const handleRemoveItem = async (cartId) => {
        try {
            setUpdating(true);
            await cartService.removeFromCart(cartId);
            await loadCart();
            setAppliedCoupon(null);
            setCouponCode('');
            toast.success('Item removed from cart');
        } catch (error) {
            toast.error(error.message || 'Failed to remove item');
        } finally {
            setUpdating(false);
        }
    };

    const buildCartItemsPayload = () =>
        cartItems.map(item => ({
            ProductCode: item.ProductCode,
            ProductName: item.ProductName,
            Subtotal: item.Subtotal,
        }));

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) { toast.error('Enter a coupon code'); return; }
        setApplyingCoupon(true);
        try {
            const res = await api.post('/coupons/apply', {
                CouponCode: couponCode.trim().toUpperCase(),
                OrderAmount: subtotal,
                cartItems: buildCartItemsPayload(),
            });
            if (res.data.success) {
                setAppliedCoupon(res.data.data);
                toast.success(
                    res.data.data.ApplicableTo === 'product'
                        ? `Coupon applied on ${res.data.data.AppliedOn}! Save ${formatINR(res.data.data.DiscountAmount)}`
                        : `Coupon applied! You save ${formatINR(res.data.data.DiscountAmount)}`
                );
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Invalid coupon code');
        } finally {
            setApplyingCoupon(false);
        }
    };

    const handleApplyCouponByCode = async (code) => {
        setApplyingCoupon(true);
        try {
            const res = await api.post('/coupons/apply', {
                CouponCode: code,
                OrderAmount: subtotal,
                cartItems: buildCartItemsPayload(),
            });
            if (res.data.success) {
                setAppliedCoupon(res.data.data);
                toast.success(
                    res.data.data.ApplicableTo === 'product'
                        ? `Applied on ${res.data.data.AppliedOn}! Save ${formatINR(res.data.data.DiscountAmount)}`
                        : `Coupon applied! Save ${formatINR(res.data.data.DiscountAmount)}`
                );
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Coupon cannot be applied');
        } finally {
            setApplyingCoupon(false);
        }
    };

    const handleRemoveCoupon = () => {
        setAppliedCoupon(null);
        setCouponCode('');
        toast.info('Coupon removed');
    };

    const handleCheckout = () => {
        if (cartItems.length === 0) {
            toast.error('Your cart is empty');
            return;
        }

        if (!isAuthenticated) {
            navigate('/login', {
                state: {
                    from: '/delivery-address',
                    appliedCoupon
                }
            });
            return;
        }

        navigate('/delivery-address', { state: { appliedCoupon } });
    };

    const getMediaUrl = (filePath) => {
        if (!filePath) return FALLBACK_IMAGE;
        if (filePath.startsWith('http')) return filePath;
        const backendUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${backendUrl}${filePath}`;
    };

    // Derived values — memoized so they only recompute when their inputs change
    const orderCoupons = useMemo(
        () => availableCoupons.filter(c => c.ApplicableTo !== 'product'),
        [availableCoupons]
    );
    const productCoupons = useMemo(
        () => availableCoupons.filter(c => c.ApplicableTo === 'product'),
        [availableCoupons]
    );
    const subtotal = useMemo(
        () => cartItems.reduce((sum, item) => sum + parseFloat(item.Subtotal), 0),
        [cartItems]
    );
    const total = appliedCoupon ? appliedCoupon.FinalAmount : subtotal;

    // ── Loading skeleton ──────────────────────────────────────────────
    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
                <Box sx={{ mb: 3, textAlign: 'center' }}>
                    <Skeleton
                        variant="text"
                        width={220}
                        height={48}
                        sx={{ mx: 'auto', bgcolor: 'rgba(188,150,94,0.12)' }}
                    />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, sm: 3 } }}>
                    <Box sx={{ flex: '1 1 auto', width: '100%' }}>
                        {[1, 2, 3].map((i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    mb: 2,
                                    p: 2,
                                    borderRadius: 1,
                                    border: '1px solid rgba(188,150,94,0.15)',
                                }}
                            >
                                <Skeleton
                                    variant="rounded"
                                    width={110}
                                    height={110}
                                    sx={{ bgcolor: 'rgba(188,150,94,0.1)', flexShrink: 0 }}
                                />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="70%" height={28} sx={{ bgcolor: 'rgba(188,150,94,0.1)' }} />
                                    <Skeleton variant="text" width="40%" sx={{ bgcolor: 'rgba(188,150,94,0.08)' }} />
                                    <Skeleton variant="text" width="30%" sx={{ bgcolor: 'rgba(188,150,94,0.08)' }} />
                                </Box>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{ width: { xs: '100%', md: 320 }, flexShrink: 0 }}>
                        <Skeleton
                            variant="rounded"
                            height={320}
                            sx={{ bgcolor: 'rgba(188,150,94,0.1)'

                             }}
                        />
                    </Box>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>

            {/* ── Header ── */}
            <Box sx={{ mb: { xs: 2, sm: 3 }, textAlign: 'center' }} className="glass-content-container">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    <ShoppingCartIcon sx={{ color: '#bc965e', fontSize: { xs: 24, sm: 32 } }} />
                    <Typography variant="h4" className="glass-title" sx={{ fontSize: { xs: '1.4rem', sm: '1.75rem', md: '2rem' } }}>
                        Shopping Cart
                    </Typography>
                </Box>
                <Typography variant="body2" className="glass-text-secondary">
                    Review your selected items before checkout
                </Typography>
            </Box>

            {cartItems.length === 0 ? (
                <Paper
                    elevation={0}
                    className="glass-empty-state"
                    sx={{ py: { xs: 5, sm: 7 }, px: { xs: 3, sm: 4 }, textAlign: 'center' }}
                >
                    <Box sx={{ mb: 2, fontSize: { xs: '3rem', sm: '4rem' } }}>🛒</Box>
                    <Typography variant="h5" gutterBottom className="glass-text-primary" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                        Your cart is empty
                    </Typography>
                    <Typography variant="body1" className="glass-text-secondary" sx={{ mb: 3, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                        Looks like you haven't added any items yet.
                    </Typography>
                    <Button
                        variant="contained"
                        className="glass-button"
                        startIcon={<ShoppingBagIcon />}
                        component={Link}
                        to="/products"
                        sx={{ px: 4 }}
                    >
                        Continue Shopping
                    </Button>
                </Paper>
            ) : (
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, sm: 3 },
                    alignItems: 'flex-start',
                }}>

                    {/* ── Cart Items ── */}
                    <Box sx={{ flex: '1 1 auto', minWidth: 0, width: '100%' }}>
                        {/* minHeight/height overrides: glass-content-container appears to carry a
                            tall default (presumably sized for fuller pages elsewhere), which leaves
                            a large empty gap under just one or two items. Forcing it to hug its
                            content here instead. */}
                        <Box className="glass-content-container" sx={{ minHeight: 'unset !important', height: 'auto !important' }}>
                            <Typography variant="h6" className="glass-title" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' } }}>
                                Cart Items ({cartItems.length})
                            </Typography>

                            {cartItems.map((item, index) => {
                                const itemProductCoupons = productCoupons.filter(
                                    c => c.ProductCode === item.ProductCode
                                );

                                return (
                                    <Fade in key={item.CartId} timeout={350} style={{ transitionDelay: `${Math.min(index, 5) * 40}ms` }}>
                                        <Box>
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    mb: 2,
                                                    background: 'rgba(188,150,94,0.05)',
                                                    border: '1px solid rgba(188,150,94,0.2)',
                                                    borderRadius: 1,
                                                    overflow: 'hidden',
                                                    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                                                    opacity: updating ? 0.6 : 1,
                                                    pointerEvents: updating ? 'none' : 'auto',
                                                    '&:hover': {
                                                        borderColor: 'rgba(188,150,94,0.45)',
                                                        boxShadow: '0 4px 18px rgba(0,0,0,0.25)',
                                                    },
                                                }}
                                            >
                                                {/* Product Image — fixed-size wrapper + native <img style>
                                                    so a global CSS reset (e.g. `img { height: auto }`)
                                                    can't collapse it down to nothing. */}
                                                <Box
                                                    sx={{
                                                        width: { xs: '100%', sm: 110, md: 130 },
                                                        height: { xs: 180, sm: 130, md: 130 },
                                                        flexShrink: 0,
                                                        overflow: 'hidden',
                                                        borderRadius: { xs: '5px 5px 0 0', sm: 1 },
                                                        border: '1px solid rgba(188,150,94,0.4)',
                                                        backgroundColor: 'rgba(255,255,255,0.04)',
                                                    }}
                                                >
                                                    <img
                                                        src={getMediaUrl(item.Photo)}
                                                        alt={item.ProductName}
                                                        onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_IMAGE; }}
                                                        style={{
                                                            display: 'block',
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                </Box>

                                                <CardContent sx={{
                                                    flex: 1,
                                                    minWidth: 0,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    p: { xs: 1.5, sm: 2 },
                                                    '&:last-child': { pb: { xs: 1.5, sm: 2 } },
                                                }}>

                                                    {/* Category chip + Price chip + Delete — one top row */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
                                                            <Chip
                                                                label={item.Category}
                                                                size="small"
                                                                sx={{ backgroundColor: 'rgba(188,150,94,0.2)', color: '#bc965e', fontSize: '0.7rem' }}
                                                            />
                                                            <Chip
                                                                label={`${formatINR(item.Price)} each`}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ borderColor: 'rgba(188,150,94,0.5)', color: '#fff', fontSize: '0.7rem' }}
                                                            />
                                                        </Box>
                                                        <Tooltip title="Remove item">
                                                            <IconButton
                                                                onClick={() => handleRemoveItem(item.CartId)}
                                                                disabled={updating}
                                                                aria-label={`Remove ${item.ProductName} from cart`}
                                                                sx={{
                                                                    color: '#ff6b6b',
                                                                    flexShrink: 0,
                                                                    p: { xs: 0.75, sm: 0.5 },
                                                                    '&:hover': { backgroundColor: 'rgba(255,107,107,0.1)' },
                                                                }}
                                                                size="small"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>

                                                    {/* Product Name */}
                                                    <Typography
                                                        variant="h6"
                                                        className="glass-text-primary"
                                                        sx={{
                                                            fontSize: { xs: '0.95rem', sm: '1.05rem' },
                                                            lineHeight: 1.3,
                                                            minWidth: 0,
                                                            mb: 1.25,
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        {item.ProductName}
                                                    </Typography>

                                                    {/* Size + Quantity */}
                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.25, alignItems: 'center' }}>

                                                        {item.Size && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography variant="body2" className="glass-text-secondary">Size:</Typography>
                                                                <FormControl size="small" sx={{ minWidth: 70 }}>
                                                                    <Select
                                                                        value={item.Size}
                                                                        onChange={(e) => handleSizeChange(item.CartId, e.target.value)}
                                                                        disabled={updating}
                                                                        inputProps={{ 'aria-label': 'Select size' }}
                                                                        sx={{
                                                                            color: '#fff',
                                                                            fontSize: '0.85rem',
                                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(188,150,94,0.5)' },
                                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(188,150,94,0.8)' },
                                                                        }}
                                                                    >
                                                                        <MenuItem value={item.Size}>{item.Size}</MenuItem>
                                                                    </Select>
                                                                </FormControl>
                                                            </Box>
                                                        )}

                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="body2" className="glass-text-secondary">Qty:</Typography>
                                                            <Box sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 0.5,
                                                                border: '1px solid rgba(188,150,94,0.3)',
                                                                borderRadius: 1,
                                                                p: 0.25,
                                                            }}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleQuantityChange(item.CartId, item.Quantity, -1)}
                                                                    disabled={updating || item.Quantity <= 1}
                                                                    aria-label="Decrease quantity"
                                                                    sx={{ color: '#bc965e', p: { xs: 0.6, sm: 0.4 } }}
                                                                >
                                                                    <RemoveIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                                <Typography
                                                                    variant="body2"
                                                                    className="glass-text-primary"
                                                                    sx={{ minWidth: 26, textAlign: 'center', fontWeight: 600 }}
                                                                >
                                                                    {item.Quantity}
                                                                </Typography>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleQuantityChange(item.CartId, item.Quantity, 1)}
                                                                    disabled={updating}
                                                                    aria-label="Increase quantity"
                                                                    sx={{ color: '#bc965e', p: { xs: 0.6, sm: 0.4 } }}
                                                                >
                                                                    <AddIcon sx={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            </Box>
                                                        </Box>
                                                    </Box>

                                                    {/* Product-specific coupon badges */}
                                                    {itemProductCoupons.length > 0 && (
                                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.25 }}>
                                                            {itemProductCoupons.map(c => {
                                                                const isApplied = appliedCoupon?.CouponCode === c.CouponCode;
                                                                return (
                                                                    <Chip
                                                                        key={c.CouponCode}
                                                                        icon={
                                                                            isApplied
                                                                                ? <CheckCircleIcon sx={{ fontSize: '14px !important', color: '#4caf50 !important' }} />
                                                                                : <CouponIcon sx={{ fontSize: '14px !important', color: '#bc965e !important' }} />
                                                                        }
                                                                        label={
                                                                            isApplied
                                                                                ? `${c.CouponCode} · Applied ✓`
                                                                                : `${c.CouponCode} · ${c.DiscountType === 'percentage' ? `${c.DiscountValue}% OFF` : `${formatINR(c.DiscountValue)} OFF`}`
                                                                        }
                                                                        size="small"
                                                                        onClick={() => {
                                                                            if (isApplied) handleRemoveCoupon();
                                                                            else { setCouponCode(c.CouponCode); handleApplyCouponByCode(c.CouponCode); }
                                                                        }}
                                                                        sx={{
                                                                            backgroundColor: isApplied ? 'rgba(76,175,80,0.12)' : 'rgba(188,150,94,0.08)',
                                                                            border: `1px dashed ${isApplied ? 'rgba(76,175,80,0.5)' : 'rgba(188,150,94,0.4)'}`,
                                                                            color: isApplied ? '#4caf50' : '#bc965e',
                                                                            fontSize: '0.65rem',
                                                                            fontFamily: 'monospace',
                                                                            fontWeight: 700,
                                                                            cursor: 'pointer',
                                                                            transition: 'transform 0.15s ease',
                                                                            '&:hover': { transform: 'translateY(-1px)' },
                                                                            '& .MuiChip-icon': { marginLeft: '6px' },
                                                                        }}
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    )}

                                                    {/* Divider + Subtotal row — pinned to bottom of the card content */}
                                                    <Divider className="glass-divider" sx={{ mt: 'auto', mb: 1 }} />
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                                        <Typography
                                                            variant="body2"
                                                            className="glass-text-secondary"
                                                        >
                                                            Subtotal
                                                        </Typography>
                                                        <Typography
                                                            variant="body1"
                                                            className="glass-text-primary"
                                                            sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1rem' } }}
                                                        >
                                                            {formatINR(getItemDisplaySubtotal(item))}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>

                                            {index < cartItems.length - 1 && (
                                                <Divider className="glass-divider" sx={{ my: 1.5, display: { xs: 'none', sm: 'block' } }} />
                                            )}
                                        </Box>
                                    </Fade>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* ── Order Summary ── */}
                    <Box sx={{
                        width: { xs: '100%', md: 300, lg: 320 },
                        flexShrink: 0,
                        position: { md: 'sticky' },
                        top: { md: 88 },
                    }}>
                        <Box className="glass-content-container">
                            <Typography variant="h6" className="glass-title" sx={{ mb: 2, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                                Order Summary
                            </Typography>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

                                {/* Coupon Section */}
                                <Box sx={{
                                    p: { xs: 1.5, sm: 2 },
                                    borderRadius: 1,
                                    border: '1px solid rgba(188,150,94,0.2)',
                                    background: 'rgba(188,150,94,0.03)',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                        <CouponIcon sx={{ color: '#bc965e', fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ color: '#bc965e', fontWeight: 600 }}>
                                            Apply Coupon
                                        </Typography>
                                    </Box>

                                    {appliedCoupon ? (
                                        <Box sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            p: 1.5, borderRadius: 1, gap: 1,
                                            background: 'rgba(76,175,80,0.1)',
                                            border: '1px dashed rgba(76,175,80,0.4)',
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18, flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography sx={{ color: '#4caf50', fontFamily: 'monospace', fontWeight: 700, fontSize: '0.85rem' }}>
                                                        {appliedCoupon.CouponCode}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', display: 'block' }}>
                                                        {appliedCoupon.ApplicableTo === 'product' ? `On: ${appliedCoupon.AppliedOn} · ` : ''}
                                                        You save {formatINR(appliedCoupon.DiscountAmount)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <IconButton
                                                size="small"
                                                onClick={handleRemoveCoupon}
                                                aria-label="Remove coupon"
                                                sx={{ color: 'rgba(255,255,255,0.5)', flexShrink: 0, '&:hover': { color: '#ff6b6b' } }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <TextField
                                                    size="small"
                                                    placeholder="Enter code"
                                                    value={couponCode}
                                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') handleApplyCoupon(); }}
                                                    inputProps={{ style: { fontFamily: 'monospace', fontWeight: 600, letterSpacing: 1 }, 'aria-label': 'Coupon code' }}
                                                    sx={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                        '& .MuiOutlinedInput-root': {
                                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                                            '& fieldset': { borderColor: 'rgba(188,150,94,0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(188,150,94,0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#bc965e' },
                                                        },
                                                        '& .MuiOutlinedInput-input': { color: 'white' },
                                                        '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
                                                    }}
                                                />
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={handleApplyCoupon}
                                                    disabled={applyingCoupon || !couponCode.trim()}
                                                    sx={{
                                                        borderColor: 'rgba(188,150,94,0.5)', color: '#bc965e', whiteSpace: 'nowrap',
                                                        '&:hover': { borderColor: '#bc965e', backgroundColor: 'rgba(188,150,94,0.1)' },
                                                        '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' },
                                                    }}
                                                >
                                                    {applyingCoupon ? <CircularProgress size={16} sx={{ color: '#bc965e' }} /> : 'Apply'}
                                                </Button>
                                            </Box>

                                            {orderCoupons.length > 0 && (
                                                <Button
                                                    size="small"
                                                    fullWidth
                                                    onClick={() => setShowCoupons(!showCoupons)}
                                                    sx={{
                                                        color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem',
                                                        textTransform: 'none', justifyContent: 'flex-start',
                                                        '&:hover': { color: '#bc965e', backgroundColor: 'transparent' },
                                                    }}
                                                >
                                                    {showCoupons ? '▲ Hide coupons' : `▼ View ${orderCoupons.length} available coupon${orderCoupons.length > 1 ? 's' : ''}`}
                                                </Button>
                                            )}

                                            {showCoupons && (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 180, overflowY: 'auto' }}>
                                                    {orderCoupons.map((c) => (
                                                        <Box
                                                            key={c.CouponCode}
                                                            onClick={() => {
                                                                setCouponCode(c.CouponCode);
                                                                setShowCoupons(false);
                                                                handleApplyCouponByCode(c.CouponCode);
                                                            }}
                                                            sx={{
                                                                p: 1.25, borderRadius: 1, cursor: 'pointer',
                                                                border: '1px dashed rgba(188,150,94,0.3)',
                                                                background: 'rgba(188,150,94,0.03)',
                                                                transition: 'all 0.2s',
                                                                '&:hover': { background: 'rgba(188,150,94,0.08)', borderColor: '#bc965e' },
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25, gap: 1 }}>
                                                                <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#bc965e', fontSize: '0.8rem' }}>
                                                                    {c.CouponCode}
                                                                </Typography>
                                                                <Chip
                                                                    label={c.DiscountType === 'percentage' ? `${c.DiscountValue}% OFF` : `${formatINR(c.DiscountValue)} OFF`}
                                                                    size="small"
                                                                    sx={{ backgroundColor: 'rgba(76,175,80,0.15)', color: '#4caf50', fontWeight: 700, fontSize: '0.6rem', height: 18 }}
                                                                />
                                                            </Box>
                                                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                                {c.MinOrderAmount > 0 && (
                                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>
                                                                        Min {formatINR(c.MinOrderAmount)}
                                                                    </Typography>
                                                                )}
                                                                {c.ExpiryDate && (
                                                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.65rem' }}>
                                                                        Expires {new Date(c.ExpiryDate).toLocaleDateString('en-IN')}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                {/* Price breakdown */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" className="glass-text-secondary">
                                        Subtotal ({cartItems.length} item{cartItems.length > 1 ? 's' : ''}):
                                    </Typography>
                                    <Typography variant="body2" className="glass-text-primary" fontWeight={500}>
                                        {formatINR(subtotal)}
                                    </Typography>
                                </Box>

                                {appliedCoupon && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body2" sx={{ color: '#4caf50' }}>
                                            Discount ({appliedCoupon.CouponCode}):
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                            − {formatINR(appliedCoupon.DiscountAmount)}
                                        </Typography>
                                    </Box>
                                )}

                                <Divider className="glass-divider" />

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h6" className="glass-text-primary" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                                        Grand Total:
                                    </Typography>
                                    <Typography variant="h6" fontWeight="700" sx={{ color: appliedCoupon ? '#4caf50' : 'white', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                                        {formatINR(total)}
                                    </Typography>
                                </Box>

                                {appliedCoupon && (
                                    <Typography variant="caption" sx={{ color: 'rgba(76,175,80,0.8)', textAlign: 'center', fontSize: '0.75rem' }}>
                                        🎉 You're saving {formatINR(appliedCoupon.DiscountAmount)} on this order!
                                    </Typography>
                                )}

                                <Button
                                    variant="contained"
                                    fullWidth
                                    className="glass-button"
                                    size="large"
                                    sx={{
                                        mt: 0.5,
                                        py: 1.2,
                                        fontWeight: 600,
                                        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                                        '&:hover': { transform: 'translateY(-1px)' },
                                    }}
                                    onClick={handleCheckout}
                                    disabled={cartItems.length === 0 || updating}
                                >
                                    Proceed to Checkout
                                </Button>

                                <Button
                                    variant="outlined"
                                    fullWidth
                                    className="glass-button-outlined"
                                    startIcon={<ShoppingBagIcon />}
                                    component={Link}
                                    to="/products"
                                >
                                    Continue Shopping
                                </Button>

                            </Box>
                        </Box>
                    </Box>

                </Box>
            )}
        </Container>
    );
};

export default CartPage;